import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { obtenerDatos } from "../../api/config";
import { GlobalContext } from "../../GlobalContext";
import { Box, Button, Typography, Alert } from "@mui/material";
import FormConfig from "./FormConfig";
import CircularProgress from "@mui/material/CircularProgress";
import { validateFieldSite } from "../../Labels/descriptions/LabelsDescriptionsRestrictions/Restrictions";
import { actualizarDato, auditPost } from "../../api/config";
import Swal from "sweetalert2";

const EditSite = () => {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const { isGranted, getModuleId, site_id } = useContext(GlobalContext);
	const [configSite, setConfigSite] = useState({});
	const [formValues, setFormValues] = useState({});
	const [errors, setErrors] = useState({});
	const [formError, setFormError] = useState("");

	useEffect(() => {
		const fetchContratos = async () => {
			setIsLoading(true); // Iniciar carga
			try {
				if (site_id !== null) {
					const datosContratos = await obtenerDatos(`/config/get-config/${site_id}`);
					setConfigSite(datosContratos);
				}
			} catch (error) {
				console.error("Error al obtener site:", error);
			} finally {
				setIsLoading(false); // Finalizar carga
			}
		};
		fetchContratos();
	}, [site_id]);

	const valuesSite = useMemo(() => {
		return {
			[t("lab_tool.site.labels.nombre")]: configSite.nombre,
			[t("lab_tool.site.labels.opmin")]: configSite.opmin,
			[t("lab_tool.site.labels.cu")]: configSite.cu,
			[t("lab_tool.site.labels.co")]: configSite.co,
			[t("lab_tool.site.labels.slmin")]: configSite.slmin,
		};
	}, [configSite, t]);

	const tooltips = [
		t("lab_tool.site.tooltips.nombre"),
		t("lab_tool.site.tooltips.opmin"),
		t("lab_tool.site.tooltips.cu"),
		t("lab_tool.site.tooltips.co"),
		t("lab_tool.site.tooltips.slmin"),
	];

	const siteFieldConfigs = {
		[t("lab_tool.site.labels.nombre")]: {
			minLength: 3,
			regex: /^(?=.*[A-Za-zÁÉÍÓÚáéíóú])[A-Za-zÁÉÍÓÚáéíóú0-9 _-]+$/,
		},
		[t("lab_tool.site.labels.opmin")]: {
			type: "number",
			allowDecimal: false, // enteros
		},
		[t("lab_tool.site.labels.cu")]: {
			type: "number",
			allowDecimal: true, // permite decimales
		},
		[t("lab_tool.site.labels.co")]: {
			type: "number",
			allowDecimal: true,
		},
		[t("lab_tool.site.labels.slmin")]: {
			type: "number",
			allowDecimal: true,
		},
	};

	useEffect(() => {
		setFormValues(valuesSite);
	}, [valuesSite]);

	const handleFieldChange = (fieldName, value) => {
		setFormValues((prev) => ({
			...prev,
			[fieldName]: value,
		}));

		const errorMsg = validateFieldSite(fieldName, value, formValues, t);

		setErrors((prevErrors) => ({
			...prevErrors,
			[fieldName]: errorMsg,
		}));
	};

	const hasErrorsOrIncompleteFields = () => {
		const fieldErrors = Object.values(errors).some((error) => error);
		const labels = Object.keys(valuesSite);
		const allFieldsFilled = labels.every(
			(label) => formValues[label] !== undefined && formValues[label] !== ""
		);

		return fieldErrors || !allFieldsFilled;
	};

	const handleUpdate = () => {
		const anyChangedField = Object.values(formValues).some((value) => value !== "");
		const labels = Object.keys(valuesSite);
		// Verificar si todos los campos tienen un valor
		const allFieldsFilled = Object.keys(formValues).length === labels.length;

		// Verificar errores específicos
		const formHasErrors = hasErrorsOrIncompleteFields();

		// Combinando las validaciones
		if (!anyChangedField || !allFieldsFilled || formHasErrors) {
			if (!anyChangedField || !allFieldsFilled) {
				setFormError(t("aux.messages.campo_obligatorio"));
			} else {
				setFormError(t("aux.messages.fix_error"));
			}
		} else {
			setFormError("");

			const datos = {
				nombre: formValues[t("lab_tool.site.labels.nombre")],
				opmin: formValues[t("lab_tool.site.labels.opmin")],
				cu: formValues[t("lab_tool.site.labels.cu")],
				co: formValues[t("lab_tool.site.labels.co")],
				slmin: formValues[t("lab_tool.site.labels.slmin")],
				site_id: site_id,
			};
			// consultar con swal si desea actualizar los datos, si confirma, se actualizan los datos, si cancela, no se actualizan
			Swal.fire({
				title: t("aux.messages.sure"),
				text: t("aux.messages.not_revert"),
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#3085d6",
				cancelButtonColor: "#d33",
				confirmButtonText: t("aux.buttons.yes"),
				cancelButtonText: t("aux.buttons.cancel"),
			}).then(async (result) => {
				if (result.isConfirmed) {
					try {
						actualizarDato(`/config/update/`, datos);

						Swal.fire(
							t("aux.messages.alert.success"),
							t("aux.messages.alert.text_alert.update"),
							"success"
						);
						const audit = getModuleId("Configuración", "Editar Site", "Actualizó configuración del Site");
						auditPost(audit, site_id);
					} catch (error) {
						console.error("Error al actualizar datos:", error);
						Swal.fire(
							t("aux.messages.alert.error"),
							t("aux.messages.alert.text_alert.error_update"),
							"error"
						);
					}
				}
			});
		}
	};

	return (
		<>
			<Box
				sx={{
					width: "100%",
					p: 2,
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Typography variant="h5" gutterBottom>
					{t("config.edit_site.title")}
				</Typography>
				<Box
					sx={{
						p: 2,
						width: "75%",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					{isLoading ? (
						<Box
							sx={{
								width: "100%",
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<CircularProgress color="primary" />
						</Box>
					) : (
						<Box
							sx={{
								width: "100%",
								p: 2,
								border: 1,
								borderColor: "secondary.main",
								borderRadius: 1,
							}}
						>
							<FormConfig
								labels={Object.keys(valuesSite)}
								handleFieldChange={handleFieldChange}
								valorDefault={formValues}
								tooltips={tooltips}
								errors={errors}
								fieldConfigs={siteFieldConfigs}
							/>
							{formError && (
								<Alert severity="error" sx={{ mb: 2 }}>
									{formError}
								</Alert>
							)}
							<Button
								sx={{ bgcolor: "primary", mb: 2, width: "100%" }}
								onClick={() => handleUpdate()}
								variant="contained"
								disabled={
									!isGranted("Configuración", "Editar Site", "Actualizó configuración del Site") ||
									hasErrorsOrIncompleteFields()
								}
							>
								{t("aux.buttons.update")}
							</Button>
						</Box>
					)}
				</Box>
			</Box>
		</>
	);
};

export default EditSite;
